import { supabase } from '../lib/supabase';

export interface CreateOperationStudentParams {
  p_first_name: string;
  p_last_name: string;
  p_branch_id?: string;
  p_program_type: string;
  p_university_name?: string;
  p_program_name?: string;
  p_created_by?: string;
  p_is_eu?: boolean;
  p_universitaly_data?: Record<string, unknown>;
  p_school_portal_data?: Record<string, unknown>;
  p_exam_data?: Record<string, unknown>;
  p_documents_status?: Record<string, unknown>;
  p_notes?: string;
  p_kesin_tercih?: string;
  p_polito_data?: Record<string, unknown>;
  p_polimi_data?: Record<string, unknown>;
}

const getProgramNote = (params: CreateOperationStudentParams) => {
  const labels: Record<string, string> = {
    polito: 'Polito Engineering',
    polimi: 'Polimi Engineering',
    'all-engineering': 'General Engineering',
    architecture: 'Architecture Mimarlık',
    imat: 'IMAT',
    medicine: 'Medicine',
    license: 'License Application'
  };
  const base = labels[params.p_program_type]
    || [params.p_program_type, params.p_program_name].filter(Boolean).join(' ');
  return params.p_notes ? `${base} - ${params.p_notes}` : base;
};

const isCreatedBySchemaError = (error: any, data: any) => {
  const message = `${error?.message || ''} ${data?.error || ''}`.toLowerCase();
  return message.includes('created_by') && message.includes('students') && message.includes('does not exist');
};

const createWithCurrentSchema = async (params: CreateOperationStudentParams) => {
  let studentId: string | null = null;
  let operationId: string | null = null;

  try {
    // The production students table predates its created_by column.
    const { data: student, error: studentError } = await supabase
      .from('students')
      .insert({
        first_name: params.p_first_name,
        last_name: params.p_last_name,
        branch_id: params.p_branch_id || null
      })
      .select('id')
      .single();

    if (studentError) throw studentError;
    studentId = student.id;

    const { data: operationStudent, error: operationError } = await supabase
      .from('operation_students')
      .insert({
        student_id: studentId,
        first_name: params.p_first_name,
        last_name: params.p_last_name,
        branch_id: params.p_branch_id || null,
        created_by: params.p_created_by || null,
        is_eu: params.p_is_eu || false,
        notes: getProgramNote(params),
        universitaly_data: params.p_universitaly_data || {},
        school_portal_data: params.p_school_portal_data || {},
        exam_data: params.p_exam_data || {},
        documents_status: params.p_documents_status || {},
        kesin_tercih: params.p_kesin_tercih || '',
        polito_data: params.p_polito_data || {},
        polimi_data: params.p_polimi_data || {}
      })
      .select('id')
      .single();

    if (operationError) throw operationError;
    operationId = operationStudent.id;

    let applicationId: string | null = null;
    if (params.p_university_name) {
      const { data: application, error: applicationError } = await supabase
        .from('university_applications')
        .insert({
          student_id: operationId,
          university: params.p_university_name,
          program: params.p_program_name || '',
          status: 'Basvuru Yapilmadi',
          created_by: params.p_created_by || null
        })
        .select('id')
        .single();

      if (applicationError) throw applicationError;
      applicationId = application.id;
    }

    return {
      data: {
        success: true,
        student_id: studentId,
        operation_id: operationId,
        application_id: applicationId
      },
      error: null
    };
  } catch (error: any) {
    // Best-effort rollback for the non-transactional compatibility path.
    if (operationId) await supabase.from('operation_students').delete().eq('id', operationId);
    if (studentId) await supabase.from('students').delete().eq('id', studentId);
    return { data: null, error };
  }
};

export const createOperationStudent = async (params: CreateOperationStudentParams) => {
  const { data, error } = await supabase.rpc('create_operation_student', params);
  if (!isCreatedBySchemaError(error, data)) return { data, error };
  return createWithCurrentSchema(params);
};
